import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Calendar, CalendarDays, CheckCircle2, Clock3, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import TurnstileWidget from '../components/TurnstileWidget';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const services = [
  'Networking',
  'IT Support',
  'Starlink Installation',
  'Web Development',
  'Security Systems',
  'TV Wall Mounting',
  'System Administration',
  'Cloud Services',
];

const quickBenefits = [
  {
    icon: Clock3,
    title: 'Quick confirmation',
    description: 'We review requests promptly and follow up to confirm the right details.',
  },
  {
    icon: CheckCircle2,
    title: 'Clear next steps',
    description: 'You will know what happens next, what we need, and how the booking moves forward.',
  },
];

const processSteps = [
  'We review your booking request',
  'Our team contacts you to confirm details',
  'We lock in the appointment and service scope',
];

const bookingHighlights = [
  {
    icon: CalendarDays,
    title: 'Flexible scheduling',
    description: 'We work with your preferred timing and help find the best available slot.',
  },
  {
    icon: Clock3,
    title: 'Fast response',
    description: 'Booking requests are reviewed quickly so you are not left waiting on the next step.',
  },
  {
    icon: CheckCircle2,
    title: 'Expert service',
    description: 'You are booking experienced support across practical IT and installation work.',
  },
];

const Booking = () => {
  const [searchParams] = useSearchParams();
  const preSelectedService = searchParams.get('service');
  const formSectionRef = useRef(null);
  const scrollTimeoutRef = useRef(null);

  const [submitting, setSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service_type: preSelectedService || '',
    message: '',
    preferred_date: '',
  });
  const [errors, setErrors] = useState({
    email: '',
    phone: '',
    date: '',
  });
  const [showErrors, setShowErrors] = useState(false);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');
    const australianMobileRegex = /^(?:\+?61|0)4\d{8}$/;
    const australianLandlineRegex = /^(?:\+?61|0)[2378]\d{8}$/;

    return australianMobileRegex.test(cleanPhone) || australianLandlineRegex.test(cleanPhone);
  };

  const validateDate = (dateString) => {
    if (!dateString) return false;

    const selectedDate = new Date(dateString);
    const now = new Date();

    if (selectedDate < now) {
      return { valid: false, message: 'Please select a future date and time' };
    }

    const sixMonthsFromNow = new Date();
    sixMonthsFromNow.setMonth(sixMonthsFromNow.getMonth() + 6);

    if (selectedDate > sixMonthsFromNow) {
      return { valid: false, message: 'Please select a date within the next 6 months' };
    }

    return { valid: true, message: '' };
  };

  const getMinDateTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const getMaxDateTime = () => {
    const maxDate = new Date();
    maxDate.setMonth(maxDate.getMonth() + 6);
    const year = maxDate.getFullYear();
    const month = String(maxDate.getMonth() + 1).padStart(2, '0');
    const day = String(maxDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}T23:59`;
  };

  const handleEmailChange = (e) => {
    const email = e.target.value;
    setFormData({ ...formData, email });

    if (showErrors && errors.email) {
      setErrors({ ...errors, email: '' });
    }
  };

  const handlePhoneChange = (e) => {
    const phone = e.target.value;
    setFormData({ ...formData, phone });

    if (showErrors && errors.phone) {
      setErrors({ ...errors, phone: '' });
    }
  };

  const handleDateChange = (e) => {
    const date = e.target.value;
    setFormData({ ...formData, preferred_date: date });

    if (showErrors && errors.date) {
      setErrors({ ...errors, date: '' });
    }
  };

  useEffect(() => {
    if (preSelectedService && formSectionRef.current) {
      scrollTimeoutRef.current = setTimeout(() => {
        formSectionRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 100);
    }

    return () => {
      clearTimeout(scrollTimeoutRef.current);
    };
  }, [preSelectedService]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    let newErrors = { email: '', phone: '', date: '' };

    if (!formData.name || !formData.email || !formData.phone || !formData.service_type || !formData.preferred_date) {
      toast.error('Please fill in all required fields');
      setShowErrors(true);
      return;
    }

    if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid Australian phone number (e.g., 04XX XXX XXX or +61 4XX XXX XXX)';
    }

    const dateValidation = validateDate(formData.preferred_date);
    if (!dateValidation.valid) {
      newErrors.date = dateValidation.message;
    }

    if (newErrors.email || newErrors.phone || newErrors.date) {
      setErrors(newErrors);
      setShowErrors(true);
      toast.error('Please correct the errors in the form');
      return;
    }

    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      toast.error('Please complete the captcha check');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        ...formData,
        turnstileToken,
      });
      toast.success('Booking request submitted successfully! We\'ll contact you soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        service_type: '',
        message: '',
        preferred_date: '',
      });
      setTurnstileToken('');
      setTurnstileResetKey((current) => current + 1);
      setErrors({ email: '', phone: '', date: '' });
      setShowErrors(false);
    } catch (error) {
      console.error('Error submitting booking:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit booking. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="overflow-hidden bg-[linear-gradient(180deg,#f7fbff_0%,#eef6ff_22%,#ffffff_50%,#f6fbff_100%)] pt-16 text-slate-900 dark:bg-[linear-gradient(180deg,#08111f_0%,#0b1729_35%,#0a1220_100%)] dark:text-white">
      <section
        className="relative isolate overflow-hidden border-b border-white/50 dark:border-white/10"
        data-testid="booking-hero"
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute left-[-8rem] top-24 h-72 w-72 rounded-full bg-cyan-300/30 blur-3xl dark:bg-cyan-400/10" />
          <div className="absolute right-[-6rem] top-12 h-80 w-80 rounded-full bg-blue-500/20 blur-3xl dark:bg-blue-500/20" />
          <div className="absolute bottom-[-5rem] left-1/3 h-64 w-64 rounded-full bg-emerald-300/20 blur-3xl dark:bg-emerald-400/10" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.8),transparent_45%)] dark:bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.08),transparent_35%)]" />
        </div>

        <div className="relative mx-auto grid min-h-[calc(100vh-4rem)] max-w-7xl items-center gap-14 px-4 py-20 sm:px-6 lg:grid-cols-[1fr_0.95fr] lg:px-8">
          <div className="max-w-3xl">
            <div className="mb-6 inline-flex items-center rounded-full border border-sky-200 bg-white/80 px-4 py-2 text-sm font-medium text-sky-700 shadow-sm backdrop-blur dark:border-sky-400/20 dark:bg-white/5 dark:text-sky-200">
              Book support, installs, and service requests across multiple regions
            </div>
            <h1 className="max-w-3xl text-4xl font-black tracking-tight text-slate-950 sm:text-5xl lg:text-7xl dark:text-white">
              Book a service with
              <span className="block bg-gradient-to-r from-sky-500 via-cyan-400 to-emerald-400 bg-clip-text text-transparent">
                less friction and faster follow-up.
              </span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-600 dark:text-slate-300">
              Send through your preferred service and timing, and we&apos;ll get back to confirm the
              best fit for your job, location, and availability.
            </p>

            {preSelectedService && (
              <div className="mt-6 inline-flex items-center gap-2 rounded-2xl border border-sky-200 bg-white/85 px-4 py-3 text-sm font-medium text-sky-800 shadow-sm backdrop-blur dark:border-cyan-300/20 dark:bg-white/5 dark:text-cyan-200">
                <span>Pre-selected service:</span>
                <span className="font-bold">{preSelectedService}</span>
              </div>
            )}

            <div className="mt-10 grid gap-4 sm:grid-cols-2">
              {quickBenefits.map((item) => (
                <div
                  key={item.title}
                  className="rounded-2xl border border-white/70 bg-white/85 p-5 shadow-[0_20px_50px_-30px_rgba(15,23,42,0.4)] backdrop-blur dark:border-white/10 dark:bg-white/5"
                >
                  <div className="flex items-start gap-4">
                    <div className="rounded-2xl bg-gradient-to-br from-sky-500 to-cyan-400 p-3 text-white">
                      <item.icon className="h-5 w-5" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-slate-950 dark:text-white">{item.title}</h2>
                      <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="absolute inset-0 rounded-[2rem] bg-gradient-to-br from-sky-400/20 via-cyan-300/10 to-emerald-300/20 blur-2xl dark:from-sky-400/15 dark:to-emerald-400/10" />
            <div className="relative overflow-hidden rounded-[2rem] border border-white/70 bg-slate-950 p-8 text-white shadow-[0_30px_80px_-24px_rgba(2,12,27,0.7)] dark:border-white/10">
              <div className="flex items-center justify-between border-b border-white/10 pb-5">
                <div>
                  <p className="text-sm uppercase tracking-[0.25em] text-sky-200/80">Connectify</p>
                  <h2 className="mt-2 text-2xl font-bold">What happens next</h2>
                </div>
                <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-sm text-sky-100">
                  Booking flow
                </div>
              </div>

              <div className="mt-6 space-y-4">
                {processSteps.map((step, index) => (
                  <div key={step} className="rounded-2xl border border-white/10 bg-white/5 p-5">
                    <div className="flex items-start gap-4">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-sky-400 to-cyan-300 text-sm font-bold text-slate-950">
                        {index + 1}
                      </div>
                      <p className="pt-2 text-sm leading-6 text-slate-200">{step}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 rounded-2xl border border-emerald-400/20 bg-emerald-400/10 p-5 text-sm text-emerald-100">
                Booking requests are reviewed carefully so we can match your service type, timing,
                and any special requirements before confirming.
              </div>
            </div>
          </div>
        </div>
      </section>

      <section ref={formSectionRef} className="py-20" data-testid="booking-form-section">
        <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8">
          <div className="mb-8 text-center">
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-sky-600 dark:text-cyan-300">
              Booking Form
            </p>
            <h2 className="mt-3 text-3xl font-black text-slate-950 sm:text-4xl dark:text-white">
              Choose your service and preferred time.
            </h2>
          </div>

          <div className="rounded-[2rem] border border-slate-200/80 bg-white/80 p-5 shadow-[0_24px_60px_-40px_rgba(15,23,42,0.35)] backdrop-blur sm:p-6 dark:border-white/10 dark:bg-white/5">
            <div className="mb-6 flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500 to-cyan-400 text-white shadow-lg">
                <Calendar className="h-6 w-6" />
              </div>
              <h3 className="text-2xl font-bold text-slate-950 dark:text-white">Service booking form</h3>
            </div>

            <form onSubmit={handleSubmit} data-testid="booking-form" className="space-y-6">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                    Your Name *
                  </label>
                  <Input
                    type="text"
                    data-testid="booking-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="John Smith"
                    required
                    className="border-slate-200 bg-white dark:border-white/10 dark:bg-white dark:text-slate-900"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                    Email Address *
                  </label>
                  <Input
                    type="email"
                    data-testid="booking-email-input"
                    value={formData.email}
                    onChange={handleEmailChange}
                    placeholder="john@example.com"
                    required
                    className={`border-slate-200 bg-white dark:border-white/10 dark:bg-white dark:text-slate-900 ${
                      showErrors && errors.email ? 'border-red-500 focus:ring-red-500' : ''
                    }`}
                  />
                  {showErrors && errors.email && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.email}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                    Phone Number *
                  </label>
                  <Input
                    type="tel"
                    data-testid="booking-phone-input"
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    placeholder="+61 4XX XXX XXX or 04XX XXX XXX"
                    required
                    className={`border-slate-200 bg-white dark:border-white/10 dark:bg-white dark:text-slate-900 ${
                      showErrors && errors.phone ? 'border-red-500 focus:ring-red-500' : ''
                    }`}
                  />
                  {showErrors && errors.phone && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.phone}</p>
                  )}
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                    Service Type *
                  </label>
                  <Select
                    value={formData.service_type}
                    onValueChange={(value) => setFormData({ ...formData, service_type: value })}
                    required
                  >
                    <SelectTrigger
                      data-testid="booking-service-select"
                      className="border-slate-200 bg-white dark:border-white/10 dark:bg-white dark:text-slate-900"
                    >
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map((service) => (
                        <SelectItem
                          key={service}
                          value={service}
                          data-testid={`service-option-${service.toLowerCase().replace(' ', '-')}`}
                        >
                          {service}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                  Preferred Date & Time *
                </label>
                <Input
                  type="datetime-local"
                  data-testid="booking-date-input"
                  value={formData.preferred_date}
                  onChange={handleDateChange}
                  min={getMinDateTime()}
                  max={getMaxDateTime()}
                  required
                  className={`border-slate-200 bg-white dark:border-white/10 dark:bg-white dark:text-slate-900 ${
                    showErrors && errors.date ? 'border-red-500 focus:ring-red-500' : ''
                  }`}
                />
                {showErrors && errors.date && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.date}</p>
                )}
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                  Additional Information
                </label>
                <Textarea
                  data-testid="booking-message-input"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Tell us more about your requirements..."
                  rows={5}
                  className="border-slate-200 bg-white dark:border-white/10 dark:bg-white dark:text-slate-900"
                />
              </div>

              {TURNSTILE_SITE_KEY && (
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                    Security Check *
                  </label>
                  <TurnstileWidget
                    siteKey={TURNSTILE_SITE_KEY}
                    onTokenChange={setTurnstileToken}
                    resetSignal={turnstileResetKey}
                  />
                  <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                    This helps protect the booking form from spam and automated abuse.
                  </p>
                </div>
              )}

              <Button
                type="submit"
                data-testid="submit-booking-btn"
                disabled={submitting}
                className="w-full bg-[#1E90FF] py-6 text-lg text-white transition duration-300 hover:bg-[#1570d6] hover:shadow-[0_20px_50px_-20px_rgba(30,144,255,0.55)]"
              >
                {submitting ? (
                  'Submitting...'
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <span>Submit Booking Request</span>
                    <Send className="h-5 w-5" />
                  </span>
                )}
              </Button>
            </form>
          </div>
        </div>
      </section>

      <section className="border-y border-slate-200/70 bg-slate-950 py-20 text-white dark:border-white/10" data-testid="booking-info">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-cyan-300">
              Why Book With Us
            </p>
            <h2 className="mt-3 text-3xl font-black sm:text-4xl">
              Booking is simple, and the follow-up is designed to stay clear.
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {bookingHighlights.map((item) => (
              <div key={item.title} className="rounded-[1.75rem] border border-white/10 bg-white/5 p-8 backdrop-blur">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-400 to-cyan-300 text-slate-950">
                  <item.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-6 text-xl font-semibold">{item.title}</h3>
                <p className="mt-3 text-sm leading-7 text-slate-300">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Booking;
